import { provider } from "@/provider.js"
import Helpers from "@/helpers.js"
import Vue from "vue"

const displayFlashOnResponse = (commit, response) => {
  if (response.error) {
    commit("displayFlashMessage", { message: response.message, severity: "error" })
  } else {
    commit("displayFlashMessage", { message: response.message, severity: "success" })
  }
}

export default {
  prepareNewBucket({ commit }) {
    commit("prepareNewBucket")
  },

  startBucketLoading({ commit }) {
    commit("requestingBucket")
  },

  stopBucketLoading({ commit }) {
    commit("receivedBucket")
  },

  clearBucket({ commit }) {
    commit("clearBucket")
  },

  checkBucketBookmark({ commit }, route) {
    const bookmarks = Vue.store.state.ui.caseWorkMenu[0].items[0].childItems
    let result = false
    bookmarks.forEach((bookmark) => {
      // Check both fullPath and path to match bookmarks with or without querystring
      if (bookmark.route === route || bookmark.route.split('?')[0] === route.split('?')[0]) {
        result = true
      }
    })
    commit("setBucketBookmarked", result)
  },

  async exportBucket({ commit }, bucketId) {
    const result = await provider.exportBucket(bucketId)
    if (result.error) {
      commit("displayFlashMessage", { message: result.message, severity: "error" })
    } else {
      commit("displayFlashMessage", { message: "A CSV export of this bucket is being prepared. Please check your notifications for when its ready.", severity: "success" })
    }
  },
  async addParticipantsToBucket({ commit }, { participants, type, bucketId }) {
    if (participants && participants.length > 0) {
      for (const participant of participants) {
        const result = await provider.addBucketMember(bucketId, participant.id, type)
        if (result.addBucketParticipant.error) {
          commit("displayFlashMessage", { message: result.addBucketParticipant.message, severity: "error" })
        } else {
          commit("addParticipantToBucket", { participant, type, bucketId })
        }
      }
    }
  },
  async removeParticipantsToBucket({ commit }, { participants, type, bucketId }) {
    if (participants && participants.length > 0) {
      for (const participant of participants) {
        const result = await provider.removeBucketMember(bucketId, participant.id, type)
        if (result.removeBucketParticipant.error) {
          commit("displayFlashMessage", { message: result.removeBucketParticipant.message, severity: "error" })
        } else {
          commit("removeParticipantFromBucket", { participant, type, bucketId })
        }
      }
    }
  },
  async setupCaseType({ commit, dispatch, rootState }, caseTypeId) {
    let caseType
    if (rootState.caseTypes.all.length === 0) {
      const result = await provider.reloadCaseTypes()
      const caseTypes = (result.account && result.account.caseTypes && Array.isArray(result.account.caseTypes))
        ? result.account.caseTypes
        : []
      commit("setCaseTypes", caseTypes)
      commit("reloadConfigMenu", caseTypes)
      caseType = Helpers.getCaseTypeById({ all: caseTypes }, caseTypeId)
      commit("setCaseType", caseType)
    } else {
      caseType = Helpers.getCaseTypeById(rootState, caseTypeId)
      dispatch("setCurrentCaseTemplate", { caseTypeId })
      commit("setCaseType", caseType)
    }
  },
  setupBucket({ commit }, bucketData) {
    commit("setCaseType", bucketData.caseType)
    commit("setBucket", bucketData.bucket)
    commit("setHeaders", bucketData.headers)
    commit("setCases", bucketData.currentCases)
    if (bucketData.caseType.tasklistTemplates) {
      commit("setTasklistTemplates", bucketData.caseType.tasklistTemplates)
    }
    commit("setSchema", bucketData.schema)
  },

  setSchema({ commit, rootState }, caseTypeId) {
    const caseType = rootState.caseTypes.all.find((caseType) => {
      return parseInt(caseType.id) === parseInt(caseTypeId)
    })
    commit("setSchema", caseType.currentSchema)
  },
  async getRelatedCases({ commit }, data) {
    commit("requestingBucket")
    const selectedSortField = data.sortField ? data.sortField : "title"
    const selectedSortDirection = data.sortDirection ? data.sortDirection : "asc"
    const relationData = await provider.findRelatedCases(Helpers.pluralize(data.caseTypeName), data.relatedData, data.page - 1, data.pageSize, selectedSortField, selectedSortDirection)
    if (!relationData.relatedCases) {
      relationData.relatedCases = []
    }
    const bucket = {
      name: "Related " + data.caseTypeName,
      description: "Related " + data.caseTypeName,
      cases: relationData.relatedCases,
      displayFields: data.relatedData.fields,
      total: relationData.total,
    }
    console.log("getRelatedCases", bucket)

    commit("setBucket", bucket)
    commit("setCases", relationData.relatedCases)
    commit("receivedBucket")
  },

  async getBucket({ commit }, data) {
    commit("requestingBucket")
    const rawSort = data.sortField
    const sortField =
      rawSort != null && String(rawSort).trim() !== "" ? String(rawSort).trim() : undefined
    // Only send sort to API when a field is set — otherwise Elasticsearch uses bucket sortFields (paginated global order).
    let sortDirection
    if (sortField) {
      sortDirection = data.sortDirection || "asc"
      if (data.descending) {
        sortDirection = "desc"
      }
    }
    try {
      const bucketData = await provider.findOrderedBucket(data.bucketId, data.page, data.pageSize, sortField, sortDirection)
      if (!bucketData || !bucketData.bucket) {
        if (typeof window !== 'undefined' && window.__CB_DEBUG_BUCKET__) {
          console.log("[buckets/getBucket] Defensive: findOrderedBucket returned no bucket", { bucketId: data.bucketId, bucketData: !!bucketData })
        }
        console.warn("[buckets/getBucket] Unable to load bucket", { bucketId: data.bucketId })
        commit("displayFlashMessage", { message: "Unable to load bucket. Please try again.", severity: "error" })
        commit("receivedBucket")
        return
      }
      if (data.caseType) commit("setCaseType", data.caseType)
      commit("setBucket", bucketData.bucket)
    } catch (err) {
      commit("displayFlashMessage", { message: err.message || "An error occurred while loading the bucket.", severity: "error" })
    } finally {
      commit("receivedBucket")
    }
  },

  async getOrderedBucket({ commit }, data) {
    commit("requestingBucket")
    const rawSort = data.sortField
    const sortField =
      rawSort != null && String(rawSort).trim() !== "" ? String(rawSort).trim() : undefined
    const sortDirection = sortField ? data.sortDirection || "asc" : undefined
    if (data.searchableBy === "title") {
      data.searchableBy = "_title"
    }
    const bucketData = await provider.findOrderedBucket(data.bucketId, data.page, data.pageSize, sortField, sortDirection, data.searchTerm, data.searchableBy)
    commit("setBucket", bucketData.bucket)
    commit("receivedBucket")
  },

  async getOrderedBucketMapPoints({ commit }, data) {
    if (data.searchableBy === "title") {
      data.searchableBy = "_title"
    }
    const bucketData = await provider.findBucketMapPoints(data.bucketId, data.searchTerm, data.searchableBy, data.mapField)
    commit("setBucketMapPoints", bucketData.bucket.mapPoints)
  },

  async findBucket({ commit }, bucketId) {
    commit("requestingBucket")
    const bucket = await provider.findBucket(bucketId)
    commit("setBucket", bucket)
    commit("receivedBucket")
    return bucket
  },

  async createBucketGroup({ commit, dispatch, rootState }, { bucketGroupPayload, selectedBuckets }) {
    const response = await provider.createBucketGroup(bucketGroupPayload)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message, severity: "error" })
    } else {
      response.caseTypeId = parseInt(rootState.cbroute.params.case_type_id)
      response.buckets = selectedBuckets
      commit("addBucketGroup", response)
      dispatch("requestCurrentUser")
    }
    return response
  },

  async updateBucketGroup({ commit, dispatch }, data) {
    const response = await provider.updateBucketGroup(data)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message, severity: "error" })
    } else {
      commit("displayFlashMessage", { message: response.message, severity: "success" })
      dispatch("requestCurrentUser")
    }
    return response
  },

  setBucketGroupBuckets({ commit, dispatch, state }, { caseTypeId, newBuckets, bucketGroupId }) {
    if (bucketGroupId) {
      commit("setBucketGroupBuckets", { caseTypeId, newBuckets, bucketGroupId })
    } else {
      const currentBucketGroup = { name: state.current.bucketGroup.name, buckets: newBuckets }
      commit("setCurrentBucketGroup", currentBucketGroup)
    }
  },

  async deleteBucketGroup({ commit, dispatch, rootState }, id) {
    const response = await provider.deleteBucketGroup(id)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message, severity: "error" })
    } else {
      const caseTypeId = parseInt(rootState.cbroute.params.case_type_id)
      const data = { caseTypeId: caseTypeId, bucketGroupId: id }
      commit("displayFlashMessage", { message: response.message, severity: "success" })
      commit("removeBucketGroup", data)
      dispatch("requestCurrentUser")
    }
    return response
  },

  setCurrentBucketGroup({ commit }, data) {
    commit("setCurrentBucketGroup", data)
  },

  async addBucketToGroup({ commit }, data) {
    const response = await provider.addBucketToGroup(data)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message, severity: "error" })
    }
    return response
  },

  async repositionBucketInGroup({ commit }, data) {
    const response = await provider.repositionBucketInGroup(data)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message, severity: "error" })
    }
    return response
  },

  async removeBucketFromGroup({ commit }, data) {
    const response = await provider.removeBucketFromGroup(data)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message, severity: "error" })
    }
    return response
  },

  async createBucket({ commit }, data) {
    const response = await provider.createBucket(data)
    displayFlashOnResponse(commit, response.data.createBucket)
    if (!response.error) {
      const newBucket = response.data.createBucket.bucket
      commit("createBucket", newBucket)
      commit("setBucket", newBucket)
      return newBucket
    }
  },

  async updateBucket({ dispatch, rootState, commit }, bucket) {
    await provider.updateBucket(bucket)
    await provider.updateBucketDisplayFields(bucket)
    await provider.createBucketDisplayFields(bucket)
    await provider.deleteBucketSortFields(bucket)
    await provider.createBucketSortFields(bucket)
    await provider.updateBucketFilterFields(bucket)
    await provider.createBucketFilterFields(bucket)
    await dispatch("findBucket", bucket.id)
    commit("receivedBucket")
    Vue.router.push({
      name: "Bucket",
      params: {
        bucket_id: bucket.id,
        case_type_id: rootState.caseTypes.current.id,
      },
    })
  },

  async updateBucketOwner({ commit }, { bucket, owner, type }) {
    const result = await provider.updateBucketOwner(bucket, owner.id, type)
    if (result.error) {
      commit("displayFlashMessage", { message: result.message, severity: "error" })
      console.error(result.message, result.lowLevelMessage)
    } else {
      commit("setBucketOwner", { bucket, owner, type })
      commit("displayFlashMessage", { message: `Changed bucket owner to ${owner.displayName}`, severity: "success" })
    }
  },

  async deleteBucket({ commit, rootState }, bucketId) {
    const caseTypeId = rootState.caseTypes.current.id
    const result = await provider.deleteBucket(bucketId)
    if (result.error) {
      commit("displayFlashMessage", { message: result.message, severity: "error" })
    } else {
      commit("deleteBucket", { caseTypeId, bucketId })
    }
  },

  async addNewDisplayField({ commit, state }) {
    let displayFields = []

    if (!state.current.bucket.displayFields) {
      displayFields = []
    } else {
      displayFields = state.current.bucket.displayFields
    }

    displayFields.push({
      fieldset: "",
      fieldName: "",
      position: state.current.bucket.displayFields.length + 1,
      sortDirection: "desc",
      sortPosition: displayFields.length,
    })
    commit("setBucketDisplayFields", displayFields)
  },

  async addNewFilterField({ commit, state }) {
    state.current.bucket.criteria.push({ fieldset: "", fieldName: "", condition: "", value: "" })
    commit("setBucketFilterFields", state.current.bucket.criteria)
  },

  async deleteDisplayField({ commit, state }, data) {
    const newDisplayFields = state.current.bucket.displayFields.filter((field) => {
      return !(field.fieldset === data.fieldset && field.fieldName === data.fieldName)
    })
    commit("setBucketDisplayFields", newDisplayFields)
  },

  async deleteFilterField({ commit, state }, data) {
    const newFilterFields = state.current.bucket.criteria.filter((field) => {
      return !(field.fieldset === data.fieldset && field.fieldName === data.fieldName && field.condition === data.condition && field.value === data.value)
    })
    commit("setBucketFilterFields", newFilterFields)
  },

  async mapSortFieldsToDisplayFields({ commit, state }) {
    const bucket = Object.assign({}, state.current.bucket)

    // Map the sort fields onto the display fields.
    if (bucket && bucket.displayFields) {
      bucket.displayFields.forEach((displayField) => {
        bucket.sortFields.forEach((sortField) => {
          const fieldNameMatches = displayField.fieldName === sortField.fieldName
          const fieldSetMatches = displayField.fieldset === sortField.fieldset

          if (fieldNameMatches && fieldSetMatches) {
            displayField.sortDirection = sortField.direction
            displayField.sortPosition = sortField.position
          }
        })
      })

      // Default the rest of the fields to something sensible.
      bucket.displayFields.forEach((displayField) => {
        if (displayField.sortDirection === undefined) {
          displayField.sortDirection = "desc"
        }

        if (displayField.sortPosition === undefined) {
          displayField.sortPosition = 0
        }
      })
      commit("setBucket", bucket)
    }
  },

  async addSortField({ commit, state }, field) {
    const currentSortFields = state.current.bucket.sortFields
    const existingSortField = currentSortFields.find((sf) => sf.fieldName === field.fieldName)
    if (existingSortField) {
      existingSortField.direction = existingSortField.direction === "asc" ? "desc" : "asc"
    } else {
      currentSortFields.push({ position: currentSortFields.length + 1, fieldset: field.fieldset, fieldName: field.fieldName, direction: "asc" })
    }
    commit("setBucketSortFields", currentSortFields)
  },

  resetSortOrder({ commit }) {
    commit("setBucketSortFields", [])
  },

  async executeBucketRunTask({ commit }, { bucket, task }) {
    const result = await provider.executeBucketRunTask(bucket.id, task.id)
    if (result.error) {
      commit("displayFlashMessage", { message: result.message, severity: "error" })
    } else {
      commit("displayFlashMessage", { message: "Running task on bucket. See Jobs for progress...", severity: "success" })
    }
  },

  async executeSelectedCaseRunTask({ commit }, { caseIds, task }) {
    const result = await provider.executeSelectedCaseRunTask(caseIds, task.id)
    if (result.error) {
      commit("displayFlashMessage", { message: result.message, severity: "error" })
    } else {
      commit("displayFlashMessage", { message: "Running task on bucket. See Jobs for progress...", severity: "success" })
    }
  },

  async reindexSortPositions({ commit, state }) {
    const bucket = Object.assign({}, state.current.bucket)
    let maxSortPosition = 0
    let minSortPosition = 99

    // Find out boundaries of our sorting fields.
    bucket.displayFields.forEach((displayField) => {
      if (displayField.sortPosition > maxSortPosition) {
        maxSortPosition = displayField.sortPosition
      }

      if (displayField.sortPosition < minSortPosition) {
        minSortPosition = displayField.sortPosition
      }
    })

    // Fix the sorting fields as we've removed the first one.
    if (minSortPosition > 1) {
      bucket.displayFields.forEach((displayField) => {
        if (displayField.sortPosition > 0) {
          displayField.sortPosition = displayField.sortPosition - 1
        }
      })
    }

    // Fix the gap in the sorting fields.
    let position

    if (minSortPosition === 1) {
      for (position = minSortPosition; position <= maxSortPosition; position++) {
        var displayField = bucket.displayFields.find((field) => field.sortPosition === position && field.sortPosition > 0)

        if (displayField === undefined) {
          var nextDisplayField = bucket.displayFields.find((field) => field.sortPosition === position + 1)
          if (nextDisplayField !== undefined) {
            nextDisplayField.sortPosition = nextDisplayField.sortPosition - 1
          }
        }
      }
    }
    commit("setBucket", bucket)
  },
  setHasChanged({ commit }, changed) {
    commit("setHasChanged", changed)
  },
  subscribeToBucketPresence(_, data) {
    provider.subscribeToBucketPresence(data.caseIds)
  },
  unsubscribeFromBucketPresence() {
    provider.unsubscribeFromBucketPresence()
  },
}
